import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { CalendarAbsenceTypeInfo, CalendarEvent } from '@/lib/absences';
import { EVENT_TYPE_FALLBACK_COLOR } from '@/lib/calendar-event-colors';
import { parseDateOnly } from '@/lib/date';
import { addMonths, format, subMonths, eachDayOfInterval, startOfMonth, endOfMonth, getDate, isWeekend, startOfDay } from 'date-fns';
import { Cake, ChevronLeft, ChevronRight, Download, FileSpreadsheet, Printer, Search, X } from 'lucide-react';
import { useRef } from 'react';
import { XLSX_BRAND as BRAND, argb } from '@/lib/xlsx-brand';

// The palette and the hex→ARGB helper live in `@/lib/xlsx-brand`, shared with
// the Time Tracker's report export, which needs the same values: two styled
// workbooks with a copy each would mean finding both on the next brand change.
// `tealSoft` and `beige` are this file's weekend tints; `beige` is also the
// report export's group-total tint, and the shared module says so, because a
// nudge made for a report's totals would land here. [HW-877]

interface Department {
  id: string;
  name: string;
}

interface EmployeeWithEvents {
  id: string;
  name: string;
  avatar: string | null;
  position: string | null;
  events: CalendarEvent[];
}

interface ScheduleToolbarProps {
  currentDate: Date;
  onDateChange: (date: Date) => void;
  departmentFilter: string;
  onDepartmentChange: (value: string) => void;
  departments: Department[];
  searchQuery: string;
  onSearchChange: (value: string) => void;
  visibleCount: number;
  totalCount: number;
  absenceTypes: CalendarAbsenceTypeInfo[];
  employees: EmployeeWithEvents[];
  showBirthdayOnly: boolean;
  onShowBirthdayOnlyChange: (value: boolean) => void;
}

// HW-719: events covering `day` (birthdays excluded), shared by both exports.
// parseDateOnly+startOfDay pin both bounds to local midnight — a raw
// new Date(isoZ) shifts forward and would drop day-1 (HR: 18-20 May → 19-20).
function eventsForDay(events: CalendarEvent[], day: Date): CalendarEvent[] {
  return events.filter((e) => {
    if (e.type === 'birthday') return false;
    const start = startOfDay(parseDateOnly(e.startsOn));
    const end = startOfDay(parseDateOnly(e.endsOn));
    return day >= start && day <= end;
  });
}

async function exportToExcel(employees: EmployeeWithEvents[], currentDate: Date) {
  // ExcelJS is dynamic-imported so it only lands in the bundle the user
  // actually clicks Export — the workbook code + style runtime is ~250 KB
  // gzipped, not worth shipping to everyone on every page load.
  const ExcelJS = (await import('exceljs')).default;

  const days = eachDayOfInterval({ start: startOfMonth(currentDate), end: endOfMonth(currentDate) });
  const monthLabel = format(currentDate, 'MMMM yyyy');
  const lastCol = 2 + days.length;

  const wb = new ExcelJS.Workbook();
  wb.creator = 'Hygge';
  wb.created = new Date();
  const ws = wb.addWorksheet(monthLabel, {
    // Freeze panes: title + header row vertically, employee + department
    // columns horizontally — scrolling sideways through 31 days keeps names
    // visible, scrolling down keeps the date row visible.
    views: [{ state: 'frozen', xSplit: 2, ySplit: 2 }],
  });

  // ── Title row ─────────────────────────────────────────────────────────
  ws.mergeCells(1, 1, 1, lastCol);
  const titleCell = ws.getCell(1, 1);
  titleCell.value = `Schedule — ${monthLabel}`;
  titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: argb(BRAND.teal) } };
  titleCell.font = { name: 'Calibri', size: 14, bold: true, color: { argb: argb(BRAND.cream) } };
  titleCell.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
  ws.getRow(1).height = 28;

  // ── Header row ────────────────────────────────────────────────────────
  // "1\nMon" so the day number reads big and the weekday sits underneath.
  // wrapText + a tall row height makes Excel honour the soft line break.
  const headerValues = ['Employee', 'Department', ...days.map((d) => `${getDate(d)}\n${format(d, 'EEE')}`)];
  const headerRow = ws.getRow(2);
  headerRow.values = headerValues;
  headerRow.height = 34;
  headerRow.eachCell((cell, col) => {
    const isWeekendCol = col > 2 && isWeekend(days[col - 3]);
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      // Subtly tint weekend day-headers so the eye doesn't wander into them
      // by accident — kept inside the brand teal family so the band reads
      // as one block.
      fgColor: { argb: argb(isWeekendCol ? BRAND.tealSoft : BRAND.teal) },
    };
    cell.font = { name: 'Calibri', size: 10, bold: true, color: { argb: argb(BRAND.cream) } };
    cell.alignment = { wrapText: true, vertical: 'middle', horizontal: 'center' };
  });

  // ── Body rows ─────────────────────────────────────────────────────────
  // Thin beige border under every body row — gives the eye a track to follow
  // sideways across 31 day columns without losing the row.
  const rowSeparator = {
    bottom: { style: 'thin' as const, color: { argb: argb(BRAND.border) } },
  };

  employees.forEach((emp, idx) => {
    const row = ws.getRow(3 + idx);
    row.height = 22;

    // Employee + Department columns get the cream tint so the "label zone"
    // visually detaches from the grid of day cells to its right.
    const nameCell = row.getCell(1);
    nameCell.value = emp.name;
    nameCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: argb(BRAND.cream) } };
    nameCell.font = { name: 'Calibri', size: 11, bold: true, color: { argb: argb(BRAND.foreground) } };
    nameCell.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
    nameCell.border = rowSeparator;

    const deptCell = row.getCell(2);
    deptCell.value = emp.position || '';
    deptCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: argb(BRAND.cream) } };
    deptCell.font = { name: 'Calibri', size: 10, color: { argb: argb(BRAND.mutedText) } };
    deptCell.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
    deptCell.border = rowSeparator;

    days.forEach((day, dIdx) => {
      const dayEvents = eventsForDay(emp.events, day);

      const cell = row.getCell(3 + dIdx);
      cell.alignment = { vertical: 'middle' as const, horizontal: 'center' as const, wrapText: true };

      if (dayEvents.length > 0) {
        // HW-719: an overlap can't share one cell's single fill/font — list every
        // type name, style by precedence (approved wins the fill, else outline).
        cell.value = dayEvents.map((e) => e.absenceTypeName || e.type).join(' / ');
        const approved = dayEvents.find((e) => e.status !== 'pending');
        const primary = approved ?? dayEvents[0];
        // HW-719: per-type brand fallback (as the month grid), not faint gray.
        const color = primary.absenceTypeColor || EVENT_TYPE_FALLBACK_COLOR[primary.type];
        if (approved) {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: argb(color) } };
          cell.font = { name: 'Calibri', size: 8, bold: true, color: { argb: argb('FFFFFF') } };
          cell.border = rowSeparator;
        } else {
          // HW-701: pending renders as an outline (no fill) with coloured text +
          // a full box border, mirroring the on-screen distinction from approved.
          const edge = { style: 'thin' as const, color: { argb: argb(color) } };
          cell.font = { name: 'Calibri', size: 8, bold: true, color: { argb: argb(color) } };
          cell.border = { top: edge, left: edge, bottom: edge, right: edge };
        }
      } else {
        // Weekend cells use the warm beige tint so the work week's emptiness
        // reads distinct from the weekend's emptiness — same hue family as
        // the cream label zone, so the spreadsheet stays in one palette.
        if (isWeekend(day)) {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: argb(BRAND.beige) } };
        }
        cell.border = rowSeparator;
      }
    });
  });

  // ── Column widths ─────────────────────────────────────────────────────
  ws.getColumn(1).width = 28;
  ws.getColumn(2).width = 22;
  days.forEach((_, i) => {
    ws.getColumn(3 + i).width = 5;
  });

  // ── Autofilter (rows below header) ───────────────────────────────────
  ws.autoFilter = {
    from: { row: 2, column: 1 },
    to: { row: 2 + employees.length, column: lastCol },
  };

  // ── Save via Blob + temp anchor (no FileSaver dep) ───────────────────
  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `schedule-${format(currentDate, 'yyyy-MM')}.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function exportToPrint(employees: EmployeeWithEvents[], currentDate: Date) {
  const days = eachDayOfInterval({ start: startOfMonth(currentDate), end: endOfMonth(currentDate) });
  const monthLabel = format(currentDate, 'MMMM yyyy');

  const headerCells = days
    .map((d) => {
      // HW-719: clearly visible weekend tint (was near-white #f1f5f9), per HW-700.
      const bg = isWeekend(d) ? 'background:#cdd8e4;' : '';
      return `<th style="padding:2px 4px;font-size:9px;text-align:center;border:1px solid #e2e8f0;${bg}">${getDate(d)}<br/><span style="font-weight:normal;font-size:8px;color:#94a3b8">${format(d, 'EEE')}</span></th>`;
    })
    .join('');

  const bodyRows = employees
    .map((emp) => {
      const cells = days
        .map((day) => {
          const dayEvents = eventsForDay(emp.events, day);

          // HW-719: stronger weekend tint than near-white #f8fafc (see header).
          const bg = isWeekend(day) ? 'background:#dbe3ec;' : '';
          if (dayEvents.length > 0) {
            // HW-719: one chip per event, stacked, so an approved+pending overlap
            // shows both (mirrors the on-screen lanes) instead of dropping one.
            const chips = dayEvents
              .map((event) => {
                // HW-719: per-type brand fallback (as the month grid does), not gray.
                const color = event.absenceTypeColor || EVENT_TYPE_FALLBACK_COLOR[event.type];
                // HW-701: approved = solid fill + white text; pending = outline
                // (transparent fill, coloured border + text), matching the screen.
                const chipStyle =
                  event.status === 'pending'
                    ? `background:transparent;color:${color};border:1px solid ${color};`
                    : `background:${color};color:white;`;
                return `<div style="${chipStyle}border-radius:3px;padding:1px 4px;font-size:8px;text-align:center;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${event.absenceTypeName || event.type}</div>`;
              })
              .join('');
            return `<td style="padding:2px;border:1px solid #e2e8f0;${bg}"><div style="display:flex;flex-direction:column;gap:2px">${chips}</div></td>`;
          }
          return `<td style="border:1px solid #e2e8f0;${bg}"></td>`;
        })
        .join('');

      return `<tr><td style="padding:4px 8px;font-size:10px;font-weight:500;border:1px solid #e2e8f0;white-space:nowrap">${emp.name}<br/><span style="font-weight:normal;font-size:9px;color:#94a3b8">${emp.position || ''}</span></td>${cells}</tr>`;
    })
    .join('');

  // HW-719: print-color-adjust:exact keeps the approved fills + weekend tint in
  // print/PDF — browsers drop backgrounds by default, else approved would vanish.
  const html = `<!DOCTYPE html><html><head><title>Schedule — ${monthLabel}</title><style>*{-webkit-print-color-adjust:exact;print-color-adjust:exact}@page{size:landscape;margin:10mm}body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;margin:0;padding:16px}table{border-collapse:collapse;width:100%}h1{font-size:16px;margin:0 0 12px}</style></head><body><h1>Schedule — ${monthLabel}</h1><table><thead><tr><th style="padding:4px 8px;text-align:left;border:1px solid #e2e8f0;min-width:140px">Employee</th>${headerCells}</tr></thead><tbody>${bodyRows}</tbody></table></body></html>`;

  // HW-719: print a hidden iframe, not a popup — Chrome names the PDF from the
  // tab's document.title when printing an iframe, but uses the about:blank URL of
  // a window.open('') popup (ignoring its title). Retitle the tab, print, restore.
  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  // Off-screen with a real size, not 0x0 (a zero-size iframe prints blank).
  iframe.style.cssText = 'position:fixed;left:-10000px;top:0;width:1024px;height:768px;border:0;';
  document.body.appendChild(iframe);
  const frameWin = iframe.contentWindow;
  if (!frameWin) {
    iframe.remove();
    return;
  }

  const previousTitle = document.title;
  let cleanedUp = false;
  const cleanup = () => {
    if (cleanedUp) return;
    cleanedUp = true;
    frameWin.removeEventListener('afterprint', cleanup);
    window.removeEventListener('afterprint', cleanup);
    document.title = previousTitle;
    iframe.remove();
  };
  // afterprint fires on the frame (Chrome) or the top window (others); listen on
  // both, cleanup drops both so no stale listener leaks the detached iframe.
  frameWin.addEventListener('afterprint', cleanup);
  window.addEventListener('afterprint', cleanup);

  frameWin.document.open();
  frameWin.document.write(html);
  frameWin.document.close();
  document.title = `Schedule — ${monthLabel}`;
  // defer one tick so the frame paints before the print snapshot (WebKit blank-page).
  setTimeout(() => {
    try {
      frameWin.focus();
      frameWin.print();
    } catch {
      // print() threw → no dialog opened, so it's safe to tidy up immediately.
      cleanup();
    }
  }, 0);
}

export function ScheduleToolbar({
  currentDate,
  onDateChange,
  departmentFilter,
  onDepartmentChange,
  departments,
  searchQuery,
  onSearchChange,
  visibleCount,
  totalCount,
  absenceTypes,
  employees,
  showBirthdayOnly,
  onShowBirthdayOnlyChange,
}: ScheduleToolbarProps) {
  const searchRef = useRef<HTMLInputElement>(null);

  return (
    <div className="flex items-center justify-between border-b border-border px-6 py-2.5">
      {/* Left */}
      <div className="flex items-center gap-3">
        {/* Month nav */}
        <div className="flex items-center gap-1.5">
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8 rounded-full"
            onClick={() => onDateChange(subMonths(currentDate, 1))}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="min-w-[130px] text-center text-sm font-semibold">
            {format(currentDate, 'MMMM yyyy')}
          </span>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8 rounded-full"
            onClick={() => onDateChange(addMonths(currentDate, 1))}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        <Button
          variant="outline"
          size="sm"
          className="h-8 text-xs"
          onClick={() => onDateChange(new Date())}
        >
          Today
        </Button>

        {/* Department filter */}
        <Select value={departmentFilter} onValueChange={onDepartmentChange}>
          <SelectTrigger className="h-8 w-[160px] text-xs">
            <SelectValue placeholder="All departments" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All departments</SelectItem>
            {departments.map((dept) => (
              <SelectItem key={dept.id} value={dept.id}>
                {dept.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Search */}
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            ref={searchRef}
            data-schedule-search
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search employee..."
            className="h-8 w-[180px] pl-8 text-xs focus-visible:w-[220px] transition-all"
          />
          {searchQuery && (
            <button
              onClick={() => {
                onSearchChange('');
                searchRef.current?.focus();
              }}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        <span className="text-xs text-muted-foreground">
          {visibleCount === totalCount
            ? `${totalCount} employees`
            : `${visibleCount} of ${totalCount}`}
        </span>

        <Button
          variant={showBirthdayOnly ? 'secondary' : 'outline'}
          size="sm"
          className="h-8 gap-1.5 text-xs"
          onClick={() => onShowBirthdayOnlyChange(!showBirthdayOnly)}
        >
          <Cake className="h-3.5 w-3.5" />
          {showBirthdayOnly ? 'All events' : 'Absences only'}
        </Button>
      </div>

      {/* Right */}
      <div className="flex items-center gap-4">
        {/* Legend */}
        <div className="flex flex-wrap items-center gap-3">
          {absenceTypes.map((type) => (
            <div key={type.id} className="flex items-center gap-1.5">
              <span
                className="h-2 w-2 rounded-sm"
                style={{ backgroundColor: type.color || '#6B7280' }}
              />
              <span className="text-[11px] text-muted-foreground">{type.name}</span>
            </div>
          ))}
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs">
              <Download className="h-3.5 w-3.5" />
              Export
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={() => {
                exportToExcel(employees, currentDate).catch((err) => {
                  console.error('xlsx export failed', err);
                });
              }}
            >
              <FileSpreadsheet className="mr-2 h-4 w-4" />
              Excel (.xlsx)
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => exportToPrint(employees, currentDate)}>
              <Printer className="mr-2 h-4 w-4" />
              Print / PDF
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
